// src/features/system/admin/api/expensesApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Expenses — FE API layer.
//
// Types + fetch functions for the expense tracker.
// All calls go through /bff/expenses/* endpoints.
// Tenant-scoped: the BFF auto-isolates data per tenant via session context.
// ────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/expenses';

// ── Types ────────────────────────────────────────────────────────────────────

export interface Expense {
  id: string;
  id_scope?: number | null;
  description: string;
  amount: number;
  currency: string;
  category: string;
  subcategory: string;
  date: string;               // YYYY-MM-DD
  month: string;              // YYYY-MM
  payment_method: string;
  vendor: string;
  notes: string;
  recurring: 'none' | 'monthly' | 'weekly' | 'yearly';
  source: string;
  active: boolean;
  created_by: string | null;
  tags: string[];
  category_confidence: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface ExpenseInput {
  id?: string;
  description: string;
  amount: number;
  currency?: string;
  category?: string;
  subcategory?: string;
  date: string;
  payment_method?: string;
  vendor?: string;
  notes?: string;
  recurring?: string;
  tags?: string[];
  source?: string;
}

export interface ExpenseListResponse {
  items: Expense[];
  next_cursor: string | null;
}

export interface CategorySummary {
  category: string;
  total: number;
  count: number;
  currency: string;
}

export interface SummaryResponse {
  month: string;
  grand_total: number;
  expense_count: number;
  categories: CategorySummary[];
}

export interface CategoryOption {
  value: string;
  label: string;
  color: string;
}

export interface CategoriesResponse {
  categories: CategoryOption[];
}

// ── React Query cache keys ────────────────────────────────────────────────

export const EXPENSES_KEYS = {
  all: ['expenses'] as const,
  list: () => [...EXPENSES_KEYS.all, 'list'] as const,
  listFiltered: (params: Record<string, string | undefined>) =>
    [...EXPENSES_KEYS.all, 'list', params] as const,
  expense: (id: string) => [...EXPENSES_KEYS.all, 'expense', id] as const,
  summary: (month?: string) => [...EXPENSES_KEYS.all, 'summary', month] as const,
  categories: () => [...EXPENSES_KEYS.all, 'categories'] as const,
};

// ── Expense endpoints ──────────────────────────────────────────────────────

export async function fetchExpenses(
  params?: { from?: string; to?: string; month?: string; category?: string; limit?: number },
): Promise<ExpenseListResponse> {
  const qs = new URLSearchParams();
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  if (params?.month) qs.set('month', params.month);
  if (params?.category) qs.set('category', params.category);
  if (params?.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return bff.get<ExpenseListResponse>(`${BASE}${q ? `?${q}` : ''}`);
}

export async function fetchExpense(id: string): Promise<Expense> {
  return bff.get<Expense>(`${BASE}/${id}`);
}

export async function upsertExpense(input: ExpenseInput): Promise<Expense> {
  return bff.post<Expense, ExpenseInput>(BASE, input);
}

export async function deleteExpense(id: string): Promise<{ deleted: boolean }> {
  return bff.del<{ deleted: boolean }>(`${BASE}/${id}`);
}

// ── Summary endpoint ───────────────────────────────────────────────────────

export async function fetchSummary(month?: string): Promise<SummaryResponse> {
  const qs = new URLSearchParams();
  if (month) qs.set('month', month);
  const q = qs.toString();
  return bff.get<SummaryResponse>(`${BASE}/summary${q ? `?${q}` : ''}`);
}

// ── Categories endpoint ────────────────────────────────────────────────────

export async function fetchCategories(): Promise<CategoriesResponse> {
  return bff.get<CategoriesResponse>(`${BASE}/categories`);
}
